import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";

import useNotification from "../../notifications/useNotifications";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import Tooltip from "../../shared/Tooltip";
import useBatchListingsMutations from "../../../data/useBatchListingsMutations";
import useOffers from "../../../data/useOffers";
import useEbay from "../../../data/useEbay";
import Modal from "../../../layouts/Modal";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import usePartDefaultCategories from "../../../data/usePartDefaultCategories";
import { PageMode } from "../../../models/PagedTable";
import { IPart } from "../../../models/Part";
import classNames from "../../../utils/classNames";
import checkEbayDisabled, { ebayChecks } from "../../../utils/checkEbayDisabled";
import { FormatType, IEbayOffer, ListingDuration } from "../../../models/EbayOffer";
import { computeEbayListingTitle } from "../EbayListingTitle";
import EditListingCoreFields from "../EditListingCoreFields";
import type { EbayListingFormValues } from "../EditListing";
import CategoryModal from "../../categoryEditor/CategoryModal";

type Props = {
  part: IPart;
  isPill?: boolean;
};

export default function UnlistedReadyStatus({ part, isPill = false }: Props) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotification();
  const { saveHandlers } = useDefaultCRUDHandlers("Offer");
  const { bulkPublish } = useBatchListingsMutations("UnlistedReadyStatus");
  const { create, update } = useOffers();
  const { policies, locations } = useEbay();
  const [listNowLoading, setListNowLoading] = useState(false);
  const [editDetailsOpen, setEditDetailsOpen] = useState(false);
  const [editCategoryDefaultsOpen, setEditCategoryDefaultsOpen] = useState(false);
  const [description, setDescription] = useState<string>("");
  const { defaultPartCategory } = usePartDefaultCategories({ part: part, enabled: editDetailsOpen });

  const getDefaultValues = () => {
    const defaults = {
      title: computeEbayListingTitle(part, part.category?.partCategoryDefaults?.[0]),
      format: FormatType.FixedPrice,
      duration: ListingDuration.GTC,
      listingStarted: DateTime.now().plus({ minutes: 30 }).toFormat("yyyy-MM-dd'T'HH:mm"),
      auctionStartPrice: 0,
      auctionReservePrice: 0,
      buyItNowPrice: 0,
      enableBestOffer: false,
      autoAcceptPrice: 0,
      fulfillmentPolicyId: undefined,
      paymentPolicyId: undefined,
      returnPolicyId: undefined,
      categoryId: undefined,
      storeCategoryId: undefined,
      locationKey: undefined,
      condition: 3000,
      conditionDescription: "Used - ",
      listNow: true,
      description: "",
    } as EbayListingFormValues;

    if (!part.offers?.[0]) {
      return defaults;
    }

    const offer = part.offers[0] as any;
    return {
      ...defaults,
      ...offer,
      ...Object.fromEntries(
        Object.entries(defaults).map(([key, value]) => [
          key,
          offer[key] !== null ? offer[key] : value,
        ])
      ),
    } as EbayListingFormValues;
  };

  const { register, getValues, setValue, watch, handleSubmit, reset } = useForm<EbayListingFormValues>({
    defaultValues: getDefaultValues(),
  });

  const readyStatus: ebayChecks = useMemo(() => {
    if (!part) {
      return { isDisabled: true, error: "No part found", errors: ["No part found"], allowEdit: false };
    };

    // var defaults = defaultPartCategory.data?.data;
    var defaults = part.category?.partCategoryDefaults[0];

    var ready = checkEbayDisabled({ part, offer: part.offers[0], partCategoryDefault: defaults, ignoreListingTime: true });

    return ready;
  }, [part]);

  const isReady = !readyStatus?.isDisabled;
  const notReadyReason = readyStatus?.error || "This part is not ready to list";

  const handleListNow = () => {
    if (!part?.id || !isReady) return;

    setListNowLoading(true);
    const body = {
      parts: [
        {
          partId: part.id,
          offerId: part.offers?.[0]?.id ?? null,
          title: computeEbayListingTitle(part, part.category?.partCategoryDefaults?.[0]),
        },
      ],
      listingStarted: DateTime.now().toISO(),
      listNow: true,
      marketplaces: {
        ebay: true,
        partPro: true,
        shopify: false,
      },
    };

    bulkPublish.mutate(body, {
      onSuccess: () => {
        addNotification({
          variant: "success",
          primaryText: "Part sent to listing queue.",
        });
        queryClient.invalidateQueries(["pagedTable", PageMode.Unlisted]);
      },
      onSettled: () => {
        setListNowLoading(false);
      },
    });
  };

  const handleSetFormat = (_: string, value: any) => {
    setValue(
      "duration",
      value === FormatType.FixedPrice
        ? ListingDuration.GTC
        : ListingDuration.Days30
    );
    setValue("format", value);
  };

  const handleSetFirstLocation = () => {
    const data = getValues();
    const offer = part.offers?.[0];

    if (!data.locationKey && locations.data && locations.data.data.locations.length > 0) {
      if (offer && offer.locationKey) return;

      const firstWithName = locations.data.data.locations.filter(l => l.name && l.name.trim() !== "")?.[0];
      setValue("locationKey", firstWithName?.id);
    }
  };

  const handleSaveEditDetails = (data: EbayListingFormValues) => {
    let partOffer = {
      ...part.offers?.[0],
      ...data,
      partDataId: part.id,
      quantity: 1,
      description: description,
    } as IEbayOffer;

    if (partOffer.enableBestOffer) {
      const partCategoryDefault = defaultPartCategory.data?.data;
      const hasAutoAcceptPrice = !!(partOffer.autoAcceptPrice && partOffer.autoAcceptPrice > 0);
      const hasAutoAcceptThreshold = !!(partCategoryDefault?.autoAcceptThreshold && partCategoryDefault.autoAcceptThreshold > 0);

      if (!partOffer.buyItNowPrice) {
        addNotification({
          primaryText: "Buy It Now Price is required if enable best offer is enabled",
          variant: "error",
        });
        return;
      }

      if (!hasAutoAcceptPrice && !hasAutoAcceptThreshold) {
        addNotification({
          primaryText: "Auto Accept Price is required if enable best offer is enabled (or set an Auto Accept Threshold on the part category defaults)",
          variant: "error",
        });
        return;
      }

      let effectiveAutoAcceptPrice = partOffer.autoAcceptPrice;
      if (!hasAutoAcceptPrice && hasAutoAcceptThreshold && partCategoryDefault?.autoAcceptThreshold) {
        effectiveAutoAcceptPrice = partOffer.buyItNowPrice - ((partOffer.buyItNowPrice * partCategoryDefault.autoAcceptThreshold) / 100);
      }

      if (effectiveAutoAcceptPrice && effectiveAutoAcceptPrice > partOffer.buyItNowPrice) {
        addNotification({
          primaryText: "Auto Accept Price cannot be higher than Buy It Now Price",
          variant: "error",
        });
        return;
      }
    } else {
      partOffer.autoAcceptPrice = null;
    }

    const onSaveSuccess = () => {
      saveHandlers.onSuccess();
      queryClient.invalidateQueries(["pagedTable", PageMode.Unlisted]);
      setEditDetailsOpen(false);
    };

    const onSaveError = () => {
      saveHandlers.onError();
    };

    if (!partOffer.id) {
      create.mutate(partOffer, {
        onSuccess: onSaveSuccess,
        onError: onSaveError,
      });
      return;
    }

    update.mutate(partOffer, {
      onSuccess: onSaveSuccess,
      onError: onSaveError,
    });
  };

  useEffect(() => {
    if (!editDetailsOpen) return;

    reset(getDefaultValues());
    setDescription(part.offers?.[0]?.description ?? "");
  }, [editDetailsOpen, part]);

  useEffect(() => {
    if (!editDetailsOpen) return;

    handleSetFirstLocation();
  }, [editDetailsOpen, locations.data, locations.isLoading, part]);

  const statusPill = (
    <span
      className={classNames(
        "inline-flex w-full max-w-full items-center justify-center rounded-full px-2.5 py-1 text-[13px] font-semibold shadow-sm ring-1 ring-inset",
        isReady
          ? "bg-emerald-100 text-emerald-800 ring-emerald-200/80"
          : "bg-amber-50 text-amber-900 ring-amber-200/90"
      )}
    >
      {isReady ? "Ready" : "Not ready"}
    </span>
  );

  if (isPill) {
    return (
      <div className="h-full flex">
        <Modal
          key={`edit-details-${part.id}`}
          open={editDetailsOpen}
          setOpen={setEditDetailsOpen}
          width="max-w-6xl"
        >
          <form onSubmit={handleSubmit(handleSaveEditDetails)}>
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-800">Edit Listing Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <EditListingCoreFields
                  part={part}
                  register={register}
                  getValues={getValues}
                  setValue={setValue}
                  watch={watch}
                  handleSetFormat={handleSetFormat}
                  defaultPartCategory={defaultPartCategory}
                  policies={policies}
                  locations={locations}
                  bestOfferValue={!!watch("enableBestOffer")}
                  isPublished={part.offers && part.offers.length > 0 && part.offers[0].publishedOn !== null}
                />
              </div>
              <div className="flex justify-end gap-3">
                <ButtonInput
                  label="Cancel"
                  isSubmit={false}
                  onClick={() => setEditDetailsOpen(false)}
                  classes="!bg-white !text-gray-700 !border !border-gray-300 hover:!bg-gray-100"
                />
                <ButtonInput
                  label="Save"
                  isSubmit={true}
                  onClick={undefined}
                  classes={undefined}
                  disabled={part.soldDate !== null}
                  isLoading={create.isLoading || update.isLoading}
                />
              </div>
            </div>
          </form>
        </Modal>
        <CategoryModal
          open={editCategoryDefaultsOpen}
          setOpen={setEditCategoryDefaultsOpen}
          category={part.category || undefined}
          categoryId={part.categoryId || undefined}
        />

        <div className="mt-1 flex w-full items-start gap-1.5 border-t border-slate-200 pt-1.5">
          <div className="flex min-w-0 flex-1 flex-col gap-1.5 rounded-lg p-1">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              Ready to list
            </p>
            {isReady ? (
              statusPill
            ) : (
              <Tooltip content={notReadyReason}>
                <span className="inline-flex w-full cursor-help">{statusPill}</span>
              </Tooltip>
            )}
          </div>
          <div className="mt-7 flex gap-x-2">
            <ButtonInput
              label="List now"
              isSubmit={false}
              onClick={handleListNow}
              disabled={!isReady || listNowLoading}
              isLoading={listNowLoading}
              classes="!h-full !min-h-[1.75rem] !px-2.5 !py-1 !text-[13px] !font-semibold !whitespace-nowrap w-full sm:w-auto"
            />
            {readyStatus.allowEdit && part.offers && part.offers.length > 0 && (
              <ButtonInput
                label="Edit Details"
                isSubmit={false}
                onClick={() => setEditDetailsOpen(true)}
                classes="!h-full !min-h-[1.75rem] !px-2.5 !py-1 !text-[13px] !font-semibold !whitespace-nowrap w-full sm:w-auto"
              />
            )}
            {readyStatus.allowEdit && (!part.offers || part.offers.length === 0) && (
              <ButtonInput
                label="Edit Defaults"
                isSubmit={false}
                onClick={() => setEditCategoryDefaultsOpen(true)}
                classes="!h-full !min-h-[1.75rem] !px-2.5 !py-1 !text-[13px] !font-semibold !whitespace-nowrap w-full sm:w-auto"
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex min-w-0 flex-col gap-2">
      <div className="flex min-w-0 flex-col rounded-lg border border-slate-200 p-2">
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          Ready to list
        </p>
        {isReady ? (
          statusPill
        ) : (
          <Tooltip content={notReadyReason}>
            <span className="inline-flex w-full cursor-help">{statusPill}</span>
          </Tooltip>
        )}
      </div>
      <div className="shrink-0">
        <ButtonInput
          label="List now"
          isSubmit={false}
          onClick={handleListNow}
          disabled={!isReady || listNowLoading}
          isLoading={listNowLoading}
          classes="!min-h-[1.75rem] !px-2.5 !py-1 !text-[13px] !font-semibold !whitespace-nowrap w-full"
        />
      </div>
      {!isReady && (
        <div className="text-[11px] text-slate-500">
          Hover status for reason
        </div>
      )}
    </div>
  );
}